import { PhoneOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	Button,
	Card,
	DatePicker,
	Divider,
	Empty,
	Form,
	Input,
	Modal,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { TextArea } = Input;

interface TrackerRow {
	device_id: number;
	customer_name: string;
	customer_phone: string;
	sold_by_retailer_name: string;
	sold_by_retailer_vendor_id: string;
	overdue_amount: number;
	overdue_installment_count: number;
	total_pending_installments: number;
	overdue_days: number;
	last_contacted_at: string | null;
	last_contact_note: string | null;
	call_log_count: number;
}

interface CallLogEntry {
	id: number;
	note: string;
	created_by: string;
	created_at: string;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const ugx = (v: number) => `UGX ${formatNumber(v)}`;

const getSeverityColor = (days: number) => {
	if (days <= 7) return 'orange';
	if (days <= 14) return 'red';
	return 'purple';
};

const getSeverityLabel = (days: number) => {
	if (days <= 7) return 'Warning';
	if (days <= 14) return 'Critical';
	return 'Severe';
};

const quickPresets: { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] = [
	{ label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
	{ label: '14 Days', value: [dayjs().subtract(14, 'day'), dayjs()] },
	{ label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
	{ label: 'All Time', value: [dayjs('2020-01-01'), dayjs()] },
];

const AgentOverdueTracker = () => {
	const [form] = Form.useForm<FilterValues>();
	const [noteForm] = Form.useForm<{ note: string }>();
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs('2020-01-01'), dayjs()],
	});
	const [activeDevice, setActiveDevice] = useState<TrackerRow | null>(null);
	const [logLimit, setLogLimit] = useState(10);
	const queryClient = useQueryClient();

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['agent-overdue-tracker', filters],
		queryFn: async () => {
			const params = {
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};
			const response = await axios.get('/admin/reports/agent-overdue-tracker', {
				withCredentials: true,
				params,
			});
			return response.data.results as { devices: TrackerRow[]; count: number };
		},
	});

	const { data: logsData, isLoading: logsLoading } = useQuery({
		queryKey: ['call-logs', activeDevice?.device_id, logLimit],
		queryFn: async () => {
			const response = await axios.get(
				`/admin/devices/${activeDevice!.device_id}/call-logs`,
				{ withCredentials: true, params: { limit: logLimit } }
			);
			return response.data.results as { logs: CallLogEntry[]; count: number };
		},
		enabled: !!activeDevice,
	});

	const submitLog = useMutation({
		mutationFn: async (note: string) => {
			await axios.post(
				`/admin/devices/${activeDevice!.device_id}/call-logs`,
				{ note },
				{ withCredentials: true }
			);
		},
		onSuccess: () => {
			noteForm.resetFields();
			queryClient.invalidateQueries({ queryKey: ['call-logs', activeDevice?.device_id], exact: false });
			queryClient.invalidateQueries({ queryKey: ['agent-overdue-tracker'] });
		},
		onError: (err) => handleAPIError(err),
	});

	const rows: TrackerRow[] = data?.devices || [];

	const renderLastContacted = (row: TrackerRow) => {
		if (!row.last_contacted_at) {
			return <Tag color="red">Never contacted</Tag>;
		}
		const days = dayjs().diff(dayjs(row.last_contacted_at), 'day');
		const color = days > 7 ? 'orange' : 'green';
		const label = days === 0 ? 'Today' : `${days}d ago`;
		return (
			<Tooltip title={row.last_contact_note || ''}>
				<Tag color={color}>{label}</Tag>
			</Tooltip>
		);
	};

	const contactSorter = (a: TrackerRow, b: TrackerRow) => {
		// null → oldest (comes first when ascending = longest without contact)
		const aVal = a.last_contacted_at ? dayjs(a.last_contacted_at).unix() : -Infinity;
		const bVal = b.last_contacted_at ? dayjs(b.last_contacted_at).unix() : -Infinity;
		return aVal - bVal;
	};

	const columns: ColumnsType<TrackerRow> = [
		{
			title: 'Device ID',
			dataIndex: 'device_id',
			key: 'device_id',
			width: 90,
			sorter: (a, b) => a.device_id - b.device_id,
		},
		{
			title: 'Customer',
			key: 'customer',
			width: 180,
			render: (_, r) => (
				<div>
					<div className="font-medium">{r.customer_name}</div>
					<div className="text-xs text-gray-500">{r.customer_phone}</div>
				</div>
			),
		},
		{
			title: 'Retailer',
			key: 'retailer',
			width: 180,
			render: (_, r) => (
				<div>
					<div className="font-medium">{r.sold_by_retailer_name}</div>
					<div className="text-xs text-gray-500">{r.sold_by_retailer_vendor_id}</div>
				</div>
			),
		},
		{
			title: 'Overdue Amount',
			dataIndex: 'overdue_amount',
			key: 'overdue_amount',
			width: 160,
			align: 'right',
			render: (v: number) => (
				<Text strong style={{ color: '#ff4d4f' }}>
					{ugx(v)}
				</Text>
			),
			sorter: (a, b) => a.overdue_amount - b.overdue_amount,
		},
		{
			title: 'Overdue / Pending',
			key: 'overdue_counts',
			width: 140,
			align: 'center',
			render: (_, r) => (
				<Tag color="red">
					{r.overdue_installment_count} / {r.total_pending_installments}
				</Tag>
			),
			sorter: (a, b) => a.overdue_installment_count - b.overdue_installment_count,
		},
		{
			title: 'Overdue Days',
			dataIndex: 'overdue_days',
			key: 'overdue_days',
			width: 160,
			align: 'center',
			render: (v: number) => (
				<Tag color={getSeverityColor(v)}>
					{v}d — {getSeverityLabel(v)}
				</Tag>
			),
			sorter: (a, b) => a.overdue_days - b.overdue_days,
		},
		{
			title: 'Last Contacted',
			key: 'last_contacted_at',
			width: 150,
			align: 'center',
			render: (_, r) => renderLastContacted(r),
			sorter: contactSorter,
			defaultSortOrder: 'ascend',
		},
		{
			title: 'Calls',
			dataIndex: 'call_log_count',
			key: 'call_log_count',
			width: 70,
			align: 'center',
			render: (v: number) => <Text type="secondary">{v}</Text>,
		},
		{
			title: '',
			key: 'action',
			width: 110,
			fixed: 'right',
			render: (_, r) => (
				<Button
					type="primary"
					size="small"
					icon={<PhoneOutlined />}
					onClick={() => {
						setActiveDevice(r);
						noteForm.resetFields();
					}}
				>
					Log Call
				</Button>
			),
		},
	];

	return (
		<AppLayout
			title="Agent Overdue Tracker"
			subtitle="Track collection calls and overdue customer contact status"
		>
			{/* Filters */}
			<Card className="mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<Form
						form={form}
						layout="inline"
						initialValues={{ date_range: [dayjs('2020-01-01'), dayjs()] }}
					>
						<Form.Item name="date_range" label="Date Range">
							<RangePicker style={{ width: 280 }} placeholder={['Start Date', 'End Date']} />
						</Form.Item>
					</Form>
					<Space wrap>
						{quickPresets.map((p) => (
							<Button
								key={p.label}
								size="small"
								onClick={() => {
									form.setFieldsValue({ date_range: p.value });
									setFilters({ date_range: p.value });
								}}
							>
								{p.label}
							</Button>
						))}
					</Space>
					<Space>
						<Button
							type="primary"
							onClick={() => setFilters(form.getFieldsValue())}
						>
							Apply
						</Button>
						<Button
							onClick={() => {
								const defaultRange: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs('2020-01-01'), dayjs()];
								form.setFieldsValue({ date_range: defaultRange });
								setFilters({ date_range: defaultRange });
							}}
						>
							Reset
						</Button>
						<Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
							Refresh
						</Button>
					</Space>
					{data && (
						<Text type="secondary" className="ml-2">
							{data.count} overdue device{data.count !== 1 ? 's' : ''}
						</Text>
					)}
				</div>
			</Card>

			{/* Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={rows}
					loading={isLoading}
					rowKey="device_id"
					scroll={{ x: 1300 }}
					pagination={{
						pageSize: 50,
						showSizeChanger: true,
						showTotal: (total) => `${total} overdue devices`,
					}}
				/>
			</Card>

			{/* Log Call Modal */}
			<Modal
				open={!!activeDevice}
				title={
					activeDevice ? (
						<span>
							Log Call — <strong>{activeDevice.customer_name}</strong>{' '}
							<Text type="secondary" style={{ fontSize: 13 }}>
								({activeDevice.customer_phone})
							</Text>
						</span>
					) : null
				}
				onCancel={() => { setActiveDevice(null); setLogLimit(10); }}
				footer={null}
				width={560}
			>
				{/* New note form */}
				<Form
					form={noteForm}
					onFinish={(values) => submitLog.mutate(values.note)}
					layout="vertical"
				>
					<Form.Item
						name="note"
						label="Call note"
						rules={[{ required: true, message: 'Please enter a note' }]}
					>
						<TextArea
							rows={3}
							placeholder="e.g. Customer promised to pay Friday, said their salary comes on 27th..."
						/>
					</Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						loading={submitLog.isPending}
						icon={<PhoneOutlined />}
					>
						Save Log
					</Button>
				</Form>

				<Divider />

				{/* Call history */}
				<Title level={5} style={{ marginBottom: 12 }}>
					Call History{' '}
					{logsData && (
						<Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal' }}>
							({logsData.count} call{logsData.count !== 1 ? 's' : ''})
						</Text>
					)}
				</Title>
				{logsLoading ? (
					<Spin />
				) : !logsData?.logs.length ? (
					<Empty description="No calls logged yet" />
				) : (
					<div className="max-h-72 overflow-y-auto space-y-3">
						{logsData.logs.map((log) => (
							<div key={log.id} className="rounded border border-gray-100 bg-gray-50 p-3">
								<div className="mb-1 flex items-center justify-between">
									<Text strong style={{ fontSize: 13 }}>
										{log.created_by}
									</Text>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{dayjs(log.created_at).format('DD MMM YYYY, h:mm A')}
									</Text>
								</div>
								<Text style={{ fontSize: 13 }}>{log.note}</Text>
							</div>
						))}
						{logsData.logs.length < logsData.count && (
							<div className="pt-1 text-center">
								<Button
									size="small"
									loading={logsLoading}
									onClick={() => setLogLimit((prev) => prev + 10)}
								>
									Load More ({logsData.count - logsData.logs.length} remaining)
								</Button>
							</div>
						)}
					</div>
				)}
			</Modal>
		</AppLayout>
	);
};

export default AgentOverdueTracker;
